import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BsArrowLeft } from "react-icons/bs";
import { Skeleton } from "../../Components/Needed";
import ticketApi from "../../Helpers/ticketApi";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import Dialog from "./Dialog";
import { Activity, Composer, Thread } from "./Thread";
import { EVENT_LABEL, PRIORITIES, PRIORITY_CLASS, PRIORITY_LABEL, STATUS_CLASS, assigneeText, clockText, dayText, when } from "./shared";
import "../../Assets/css/tickets.css";

// A customer ticket for the team (Phase 7.3, 7.5, 7.9): thread + internal notes + SLA + activity,
// and Take / Reassign / Return / Appeal / Resolve. Notes are never shown to the customer.
const TeamView = ({ data, load }) => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const { ticket: t, can = {}, messages = [], events = [] } = data;
    const [dialog, setDialog] = useState(null);
    const [text, setText] = useState("");
    const [people, setPeople] = useState([]);
    const [person, setPerson] = useState("");
    const [busy, setBusy] = useState(false);
    const s = t.slaState || {};
    const clocks = [clockText(s.response, "First reply", s.paused), clockText(s.resolution, "Resolution", s.paused)].filter(Boolean);

    const open = async (kind) => {
        setText(""); setPerson(""); setDialog(kind);
        if (kind === "reassign" && !people.length) {
            const r = await ticketApi.handlers();
            if (r.ok) setPeople((r.data.people || []).filter((p) => !(t.assignee?.type === "staff" && p.id === t.assignee.id))); else toast.error(r.message);
        }
    };
    const run = async (work, done = "Done", after) => {
        setBusy(true);
        const result = await work();
        setBusy(false);
        if (!result.ok) { toast.error(result.message); if (["stale", "taken"].includes(result.code)) load(true); return result; }
        toast.success(result.message || done);
        setDialog(null);
        if (after) after(result); else await load(true);
        return result;
    };

    return (
        <>
            <div className="tk-head">
                <div>
                    <div className="small text-muted">{t.ticketId} · {t.category}{t.reason ? ` / ${t.reason}` : ""}</div>
                    <h4 className="fw-semibold mb-1">{t.subject}</h4>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <span className={`tk-status ${STATUS_CLASS[t.status] || ""}`}>{t.status}</span>
                        <span className={`tk-priority ${PRIORITY_CLASS[t.priority] || ""}`}>{PRIORITY_LABEL[t.priority]}</span>
                        {t.appeal?.active ? <span className="tk-flag">With the admins</span> : null}
                    </div>
                </div>
                <div className="tk-actions">
                    {can.take ? <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => run(() => ticketApi.take(t.ticketId), "It's yours")}>Take</button> : null}
                    {can.reassign ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => open("reassign")}>Reassign</button> : null}
                    {can.returnToPool ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => open("return")}>Return to pool</button> : null}
                    {can.appeal ? <button type="button" className="btn btn-outline-warning btn-sm" onClick={() => open("appeal")}>Appeal to admin</button> : null}
                    {can.setStatus && t.status === "In Progress" ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => run(() => ticketApi.status(t.ticketId, "Waiting on customer"))}>Waiting on customer</button> : null}
                    {can.setStatus && t.status === "Waiting on customer" ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => run(() => ticketApi.status(t.ticketId, "In Progress"))}>Back to In Progress</button> : null}
                    {can.resolve ? <button type="button" className="btn btn-success btn-sm" onClick={() => open("resolve")}>Resolve</button> : null}
                    {can.reopen ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => open("reopen")}>Reopen</button> : null}
                </div>
            </div>

            <div className="tk-grid">
                <section className="tk-main" aria-label="Conversation">
                    <Thread ticket={t} messages={messages} />
                    {t.status === "Resolved" ? <div className="alert alert-light border small mt-3">Resolved {when(t.resolvedAt)}. Waiting for the customer to confirm; it closes on its own.</div> : null}
                    {t.status === "Closed" ? <div className="alert alert-light border small mt-3">Closed {when(t.closedAt)}.</div> : null}
                    {t.appeal?.active ? <div className="alert alert-warning small mt-3">This ticket is with the admins. You'll be told when they decide.</div> : null}
                    <Composer
                        canReply={!!can.reply}
                        canNote={!!can.note}
                        canWait={!!can.setStatus}
                        onSend={async (fields, files) => {
                            const r = await ticketApi.message(t.ticketId, fields, files);
                            if (!r.ok) toast.error(r.message); else await load(true);
                            return r;
                        }}
                    />
                    {can.take ? <p className="small text-muted mt-2">Take the ticket to reply to the customer. You can still add a note.</p> : null}
                </section>

                <aside className="tk-side">
                    <div className="tk-card">
                        <h6>Details</h6>
                        <dl className="tk-dl">
                            <dt>Customer</dt><dd>{t.raisedBy?.name || ""} ({t.raisedBy?.id})</dd>
                            {t.bookingId ? <><dt>Booking</dt><dd>{hasPermission("booking.view") ? <Link to={`/booking/details/${t.bookingId}`}>{t.bookingId}</Link> : t.bookingId}</dd></> : <><dt>Type</dt><dd>Account (no booking)</dd></>}
                            <dt>With</dt><dd>{assigneeText(t)}</dd>
                            <dt>Raised</dt><dd>{when(t.createdAt)}</dd>
                            <dt>Priority</dt>
                            <dd>
                                {can.priority ? (
                                    <select className="form-select form-select-sm" aria-label="Priority" value={t.priority} disabled={busy} onChange={(e) => run(() => ticketApi.priority(t.ticketId, e.target.value), "Priority changed")}>
                                        {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
                                    </select>
                                ) : PRIORITY_LABEL[t.priority]}
                            </dd>
                        </dl>
                        {t.appeal?.at ? (
                            <div className="tk-appeal">
                                <div className="fw-semibold small">{t.appeal.active ? "Appealed" : `Appeal ${t.appeal.outcome || ""}`}</div>
                                <div className="small">{t.appeal.reason}</div>
                                {t.appeal.note ? <div className="small text-muted">Admin: {t.appeal.note}</div> : null}
                            </div>
                        ) : null}
                    </div>
                    <div className="tk-card">
                        <h6>SLA</h6>
                        {clocks.map((c) => <div key={c.text} className={`tk-sla tk-sla-${c.tone} d-block mb-1`}>{c.text}</div>)}
                        <div className="small text-muted mt-1">Waiting on the customer doesn't count.</div>
                    </div>
                    <div className="tk-card">
                        <h6>Activity</h6>
                        <Activity events={events} labels={EVENT_LABEL} />
                    </div>
                </aside>
            </div>

            {dialog === "resolve" ? (
                <Dialog title="Resolve the ticket" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-success" disabled={busy || text.trim().length < 2} onClick={() => run(() => ticketApi.resolve(t.ticketId, text.trim()), "Resolved")}>Resolve</button>}>
                    <label className="form-label small" htmlFor="tk-resolution">What was done (sent to the customer)</label>
                    <textarea id="tk-resolution" className="form-control" rows={4} maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} />
                    <p className="small text-muted mt-2 mb-0">The customer can confirm it's solved or say it isn't; otherwise it closes on its own.</p>
                </Dialog>
            ) : null}
            {dialog === "reassign" ? (
                <Dialog title="Reassign" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-primary" disabled={busy || !person} onClick={() => run(() => ticketApi.reassign(t.ticketId, person, text.trim()), "Reassigned", () => navigate("/tickets?queue=mine"))}>Reassign</button>}>
                    <label className="form-label small" htmlFor="tk-person">To (any hub)</label>
                    <select id="tk-person" className="form-select" value={person} onChange={(e) => setPerson(e.target.value)}>
                        <option value="">Choose...</option>
                        {people.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.hubId}</option>)}
                    </select>
                    <label className="form-label small mt-3" htmlFor="tk-note">Note for them (optional)</label>
                    <input id="tk-note" className="form-control" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                </Dialog>
            ) : null}
            {dialog === "return" ? (
                <Dialog title="Return to the pool" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-primary" disabled={busy} onClick={() => run(() => ticketApi.returnToPool(t.ticketId, text.trim()), "Back in the pool", () => navigate("/tickets"))}>Return</button>}>
                    <p className="small">Someone else at the hub can take it.</p>
                    <label className="form-label small" htmlFor="tk-return">Note (optional)</label>
                    <input id="tk-return" className="form-control" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                </Dialog>
            ) : null}
            {dialog === "appeal" ? (
                <Dialog title="Appeal to the admins" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-warning" disabled={busy || text.trim().length < 5} onClick={() => run(() => ticketApi.appeal(t.ticketId, text.trim()), "Sent to the admins")}>Appeal</button>}>
                    <p className="small">The ticket moves to the admins. You stay informed and they can send it back to you.</p>
                    <label className="form-label small" htmlFor="tk-appeal">Why does it need an admin?</label>
                    <textarea id="tk-appeal" className="form-control" rows={3} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                </Dialog>
            ) : null}
            {dialog === "reopen" ? (
                <Dialog title="Reopen the ticket" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-primary" disabled={busy} onClick={() => run(() => ticketApi.reopen(t.ticketId, text.trim()), "Reopened")}>Reopen</button>}>
                    <p className="small">Possible until {dayText(can.reopenUntil)}.</p>
                    <label className="form-label small" htmlFor="tk-reopen">Why (optional)</label>
                    <input id="tk-reopen" className="form-control" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                </Dialog>
            ) : null}
        </>
    );
};

// A ticket this staff member raised to the admins (Phase 7.7).
const OwnView = ({ data, load }) => {
    const t = data.ticket;
    const can = t.can || {};
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState("");
    const run = async (work, done) => {
        setBusy(true);
        const r = await work();
        setBusy(false);
        if (!r.ok) { toast.error(r.message); return; }
        toast.success(done);
        setReason("");
        load(true);
    };
    return (
        <>
            <div className="tk-head">
                <div>
                    <div className="small text-muted">{t.ticketId} · {t.category}{t.reason ? ` / ${t.reason}` : ""}</div>
                    <h4 className="fw-semibold mb-1">{t.subject}</h4>
                    <span className={`tk-status ${STATUS_CLASS[t.status] || ""}`}>{t.status}</span>
                    <span className="small text-muted ms-2">{t.handler ? `With ${t.handler}` : "Waiting for an admin"}</span>
                </div>
            </div>
            <section className="tk-main" aria-label="Conversation">
                <Thread ticket={{ ...t, raisedBy: { type: "staff", name: "You" } }} messages={data.messages || []} raiserType="staff" />
                {can.confirm ? (
                    <div className="alert alert-success mt-3">
                        <div className="fw-semibold mb-2">The admins marked this resolved. Is it sorted?</div>
                        <div className="d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-success btn-sm" disabled={busy} onClick={() => run(() => ticketApi.confirm(t.ticketId, true), "Closed. Thanks!")}>Yes, solved</button>
                            <input className="form-control form-control-sm" style={{ maxWidth: 320 }} placeholder="What's still wrong? (optional)" aria-label="What's still wrong" value={reason} onChange={(e) => setReason(e.target.value)} />
                            <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => run(() => ticketApi.confirm(t.ticketId, false, reason.trim()), "Reopened")}>Not solved</button>
                        </div>
                    </div>
                ) : null}
                {can.reopen && t.status === "Closed" ? (
                    <div className="d-flex flex-wrap gap-2 mt-3 align-items-center">
                        <input className="form-control form-control-sm" style={{ maxWidth: 320 }} placeholder="Why reopen? (optional)" aria-label="Why reopen" value={reason} onChange={(e) => setReason(e.target.value)} />
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => run(() => ticketApi.reopen(t.ticketId, reason.trim()), "Reopened")}>Reopen</button>
                        <span className="small text-muted">until {dayText(can.reopenUntil)}</span>
                    </div>
                ) : null}
                <Composer canReply={!!can.reply} canNote={false} raiserLabel="the admins" onSend={async (fields, files) => {
                    const r = await ticketApi.message(t.ticketId, { body: fields.body }, files);
                    if (!r.ok) toast.error(r.message); else await load(true);
                    return r;
                }} />
            </section>
        </>
    );
};

const TicketDetail = () => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [missing, setMissing] = useState(false);

    const load = useCallback(async (quiet) => {
        const r = await ticketApi.detail(ticketId);
        if (!r.ok) { if (!quiet) { setMissing(true); toast.error(r.message); } return; }
        setData(r.data);
    }, [ticketId]);
    useEffect(() => { setData(null); setMissing(false); load(); }, [load]);
    useEffect(() => { const timer = setInterval(() => load(true), 30000); return () => clearInterval(timer); }, [load]);

    return (
        <div className="tk-page">
            <button type="button" className="btn btn-link px-0 mb-2" onClick={() => navigate(-1)}><BsArrowLeft aria-hidden="true" /> Back</button>
            {missing ? <p className="text-muted">This ticket was not found, or you can't open it.</p>
                : !data ? <Skeleton height={420} />
                    : data.events ? <TeamView data={data} load={load} /> : <OwnView data={data} load={load} />}
        </div>
    );
};

export default TicketDetail;
